import { useState, useEffect } from "react";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Check, Clipboard } from "lucide-react";
import { UseTheme } from "../../ThemeProvider";

interface CodeBlockCardProps {
    language: string;
    code: string;
    isStreaming?: boolean;
}

const CodeBlockCard = ({ language, code, isStreaming = false }: CodeBlockCardProps) => {
    const [copied, setCopied] = useState(false);
    const { theme } = UseTheme();

    // Reset copied state after 2 seconds
    useEffect(() => {
        if (copied) {
            const timeout = setTimeout(() => setCopied(false), 2000);
            return () => clearTimeout(timeout);
        }
    }, [copied]);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(code);
        setCopied(true);
    };

    return (
        <div className="group relative my-4  rounded-lg border">
            {/* Language label + copy button header */}
            <div className="flex items-center justify-between rounded-t-lg border-b bg-sidebar px-4 py-2 text-sm dark:bg-[#21252b] dark:text-gray-300">
                <span className="font-mono">{language}</span>
                <button
                    onClick={handleCopy}
                    className="flex items-center text-gray-500 transition hover:text-zinc-900 dark:hover:text-white"
                    aria-label="Copy code"
                >
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Clipboard className="h-4 w-4" />}
                </button>
            </div>

            {/* Syntax highlighted code */}
            <div className={`${isStreaming ? "streaming-code" : ""} overflow-hidden rounded-b-lg`}>
                <SyntaxHighlighter
                    language={language}
                    style={theme === "dark" ? oneDark : oneLight}
                    customStyle={{
                        margin: 0,
                        padding: "1rem",
                        fontSize: "0.9rem",
                        lineHeight: "1.5",
                        borderRadius: 0,
                    }}
                    wrapLines={true}
                    showLineNumbers={true}
                >
                    {code}
                </SyntaxHighlighter>
            </div>
        </div>
    );
};

export default CodeBlockCard;
